import { useState } from 'react';
import { Mail, CheckCircle, ArrowRight, Clock, MessageSquare, Search, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';

interface VerificationRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: 'transcription' | 'chat' | 'research';
  currentLimit: number;
  upgradeBonus: number;
  userEmail?: string;
}

export function VerificationRequiredModal({
  isOpen,
  onClose,
  limitType,
  currentLimit,
  upgradeBonus,
  userEmail,
}: VerificationRequiredModalProps) {
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const limitConfig = {
    transcription: {
      icon: Clock,
      label: 'transcription minutes',
      currentText: `${currentLimit} minutes`,
      bonusText: `+${upgradeBonus} minutes`,
      totalText: `${currentLimit + upgradeBonus} minutes`,
    },
    chat: {
      icon: MessageSquare,
      label: 'chat messages',
      currentText: `${currentLimit} messages`,
      bonusText: `+${upgradeBonus} messages`,
      totalText: `${currentLimit + upgradeBonus} messages`,
    },
    research: {
      icon: Search,
      label: 'research units',
      currentText: `${currentLimit} units`,
      bonusText: `+${upgradeBonus} units`,
      totalText: `${currentLimit + upgradeBonus} units`,
    },
  };

  const config = limitConfig[limitType];
  const Icon = config.icon;

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendStatus('idle');

    try {
      await apiFetch('/auth/resend-verification', { method: 'POST' });
      setResendStatus('success');
    } catch (error) {
      setResendStatus('error');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Verify Your Email</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current limit info */}
          <div className="flex items-center gap-3 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
            <Icon className="w-8 h-8 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm text-zinc-400">You've reached your</p>
              <p className="text-white font-medium">
                {config.currentText} limit
              </p>
            </div>
          </div>

          {/* Upgrade preview */}
          <div className="space-y-3">
            <p className="text-sm text-zinc-300">
              Verify your email to unlock full beta access:
            </p>
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">{config.bonusText} {config.label}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <ArrowRight className="w-4 h-4" />
              <span>Total: {config.totalText}</span>
            </div>
          </div>

          {/* Email info */}
          {userEmail && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-300">
                We sent a verification email to:
              </p>
              <p className="text-white font-medium mt-1">{userEmail}</p>
            </div>
          )}

          {/* Resend status */}
          {resendStatus === 'success' && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-sm text-green-400">
                ✓ Verification email sent! Check your inbox (and spam folder).
              </p>
            </div>
          )}
          {resendStatus === 'error' && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">
                Failed to resend. Please try again later.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 p-4 border-t border-zinc-700">
          <button
            onClick={handleResendVerification}
            disabled={isResending || resendStatus === 'success'}
            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isResending ? (
              <>
                <span className="animate-spin">⟳</span>
                Sending...
              </>
            ) : resendStatus === 'success' ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Email Sent
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Resend Verification Email
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 px-4 text-zinc-400 hover:text-white text-sm transition-colors"
          >
            I'll do this later
          </button>
        </div>
      </div>
    </div>
  );
}

export default VerificationRequiredModal;
